import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
} from "~/components/ui/combobox";
import {
  ProjectTaskPickerItem,
  ProjectTaskPickerVisualRenderer,
  type ProjectTaskPickerOption,
} from "./ProjectTaskPicker";

export interface ProjectTaskAssigneesFieldProps {
  readonly ariaLabel: string;
  /** Ordered chip values, human first then agents — see assignmentSelectionValues. */
  readonly values: readonly string[];
  readonly options: readonly ProjectTaskPickerOption[];
  readonly emptyLabel: string;
  readonly disabled: boolean;
  /**
   * Fires once per chip that changed, not with the whole next selection. The
   * write path is per-subject add/remove, so handing it a full set would force
   * the caller to re-derive the diff that the toggle already tells us.
   */
  readonly onToggle: (value: string) => void;
}

/**
 * Assignees as removable chips: at most one human, any number of agents.
 *
 * Multi-select is the point — the agent picking work up is usually not the only
 * party on the task. A single-value picker forced every new assignee to evict
 * the last one, which silently un-assigned whoever was already working.
 */
export function ProjectTaskAssigneesField(props: ProjectTaskAssigneesFieldProps) {
  const labelByValue = new Map(
    props.options.map((option) => [option.value, option.label] as const),
  );
  // Chips show the name alone; the agent id belongs in the dropdown, where it
  // disambiguates two agents with the same display name.
  const chipLabelByValue = new Map(
    props.options.map((option) => [option.value, option.shortLabel ?? option.label] as const),
  );
  const visualByValue = new Map(
    props.options.map((option) => [option.value, option.visual] as const),
  );
  const groups = Array.from(
    new Set(
      props.options.map((option) => option.group).filter((group): group is string => !!group),
    ),
  );
  const ungrouped = props.options.filter((option) => !option.group);

  return (
    <Combobox
      multiple
      items={props.options.map((option) => option.value)}
      value={[...props.values]}
      itemToStringLabel={(item) => labelByValue.get(item) ?? item}
      itemToStringValue={(item) => labelByValue.get(item) ?? item}
      autoHighlight
      disabled={props.disabled}
      onValueChange={(next) => {
        // Diff against the props rather than trusting order: the primitive
        // reports the whole next set, and exactly one entry moves per commit.
        const nextValues = next as readonly string[];
        const added = nextValues.find((value) => !props.values.includes(value));
        const removed = props.values.find((value) => !nextValues.includes(value));
        const changed = added ?? removed;
        if (changed !== undefined) props.onToggle(changed);
      }}
    >
      <ComboboxChips className="border-transparent bg-transparent shadow-none hover:border-border hover:bg-muted focus-within:border-ring">
        {props.values.map((value) => {
          const visual = visualByValue.get(value);
          return (
            <ComboboxChip key={value} aria-label={labelByValue.get(value) ?? value}>
              {visual ? (
                <span className="me-1 flex shrink-0 items-center">
                  <ProjectTaskPickerVisualRenderer visual={visual} />
                </span>
              ) : null}
              <span className="truncate">{chipLabelByValue.get(value) ?? value}</span>
            </ComboboxChip>
          );
        })}
        <ComboboxChipsInput
          aria-label={props.ariaLabel}
          size="sm"
          placeholder={props.values.length === 0 ? props.emptyLabel : undefined}
        />
      </ComboboxChips>
      <ComboboxPopup>
        <ComboboxEmpty>No matches</ComboboxEmpty>
        <ComboboxList>
          {ungrouped.map((option) => (
            <ComboboxItem key={option.value} value={option.value} disabled={option.disabled}>
              <ProjectTaskPickerItem label={option.label} option={option} />
            </ComboboxItem>
          ))}
          {groups.map((group) => (
            <ComboboxGroup key={group}>
              <ComboboxGroupLabel>{group}</ComboboxGroupLabel>
              {props.options
                .filter((option) => option.group === group)
                .map((option) => (
                  <ComboboxItem key={option.value} value={option.value} disabled={option.disabled}>
                    <ProjectTaskPickerItem label={option.label} option={option} />
                  </ComboboxItem>
                ))}
            </ComboboxGroup>
          ))}
        </ComboboxList>
      </ComboboxPopup>
    </Combobox>
  );
}
